import {
  Button,
  Icon,
  Loader,
  Spacer,
  SvgBarChart,
  SvgComment,
  SvgSmileAlt,
  Text,
} from '@loomhq/lens';
import cn from 'classnames';
import pluralize from 'pluralize';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  endCTAFlow,
  endThumbnailFlow,
  endVideoSettingsFlow,
} from 'actions/editor';
import { updateTheaterMode } from 'actions/videoPlayer';
import {
  selectInsightsView,
  toggleViewerInsightsUI,
} from 'actions/viewerInsights';
import { SEE_ENGAGEMENT_UPGRADE_BUTTON_CLICKED } from 'constants/events';
import { PLANS_AND_BILLING } from 'constants/routes';
import {
  ENGAGEMENT_INSIGHTS_ACCESS,
  ENGAGEMENT_INSIGHTS_PAYWALL,
} from 'constants/scopes';
import { ENGAGEMENT_INSIGHTS_UPGRADE_MESSAGE } from 'constants/upgradeMessages';
import { leaveTrimModeCreator } from 'creators/trimming';
import { track } from 'utilities/analytics';

import SrcEngagementInsightsNoViews from 'img/engagement-insights-no-views.svg';
import { useGetSelectedWorkspace } from 'hooks/workspace';
import { hasScope } from 'selectors/scopes';
import { selectUserDataForPaywall } from 'selectors/user';
import { selectCurrentVideoId, selectVideoViews } from 'selectors/videoPlayer';
import {
  selectIsActive,
  selectViewersForEngagementInsights,
  selectViewersForViewerInsights,
} from 'selectors/viewerInsights';

import useFetchAndPopulateViewerInsights from './useFetchAndPopulateViewerInsights';
import { trackActivate } from './analytics';
import { ViewerAvatar } from './components';

import UpgradeTooltip from 'components/upgrade-tooltip';

import styles from './modal.module.less';

export const EMPTY_TITLE =
  'Share your video with someone to see their engagement!';

const MAX_DISTINCT_VIEWERS = 100;
const ModalContainer = ({ children, variant = '' }) => {
  return (
    <div
      className={cn(styles.modal, {
        [styles.small]: variant === 'small',
      })}
    >
      {children}
    </div>
  );
};

const ConditionalWrapper = ({ condition, wrapper, children }) =>
  condition ? wrapper(children) : children;

const PaywallTooltipWrapper = ({ children }) => {
  /** @type {{userId: string, userEmail: string}} */
  const { userId, userEmail } = useSelector(selectUserDataForPaywall);
  const { id: workspaceId } = useGetSelectedWorkspace();

  return (
    <UpgradeTooltip
      placement="bottomCenter"
      title="See Engagement Insights"
      message={ENGAGEMENT_INSIGHTS_UPGRADE_MESSAGE}
    >
      <Spacer top={1} />
      <a
        tabIndex={0}
        className="block"
        data-testid="engagement-insights-paywall"
        href={PLANS_AND_BILLING}
        onClick={() => {
          track(SEE_ENGAGEMENT_UPGRADE_BUTTON_CLICKED, {
            userId,
            userEmail,
            workspaceId,
          });
        }}
      >
        {children}
      </a>
    </UpgradeTooltip>
  );
};

const InsightsNoView = () => (
  <ModalContainer variant="small">
    <article className="px:large py:medium my:large flex flexDirection:column items:center">
      <img
        src={SrcEngagementInsightsNoViews}
        width={80}
        height={80}
        className="mb:large"
        alt="No views yet"
      />
      <Text alignment="center" fontWeight="medium">
        {EMPTY_TITLE}
      </Text>
    </article>
  </ModalContainer>
);

const SeeEngagementButton = ({
  hasEngagementInsights,
  showPaywall,
  openInsights,
}) => {
  if (!hasEngagementInsights && !showPaywall) {
    return null;
  }

  return (
    <div className="borderTop pt:medium px:large my:medium">
      <ConditionalWrapper
        condition={showPaywall}
        wrapper={children => (
          <PaywallTooltipWrapper>{children}</PaywallTooltipWrapper>
        )}
      >
        <Button
          onClick={openInsights}
          hasFullWidth
          isDisabled={!hasEngagementInsights}
          icon={<SvgBarChart />}
        >
          See Engagement
        </Button>
      </ConditionalWrapper>
    </div>
  );
};

export const ViewerItem = ({ viewer, hasEngagementInsights }) => {
  const { commentCount, reactionCount, user } = viewer;

  return (
    <article
      className={cn(styles.viewerItem, {
        [styles.isClickable]: hasEngagementInsights,
      })}
      onClick={viewer.onClick}
    >
      <ViewerAvatar {...user} />

      <h4 className="ellipsis weight:bold">{user.name || 'Anonymous'}</h4>
      {hasEngagementInsights && (
        <ul className={styles.reactionsAndComments}>
          <li>
            <Icon
              icon={<SvgComment />}
              color="bodyDimmed"
              altText="comments count"
            />
            <label>{commentCount}</label>
          </li>
          <li>
            <Icon
              icon={<SvgSmileAlt />}
              color="bodyDimmed"
              altText="reactions count"
            />
            <label>{reactionCount}</label>
          </li>
        </ul>
      )}
    </article>
  );
};

export default ({ closeModal }) => {
  const dispatch = useDispatch();

  const appState = useSelector(state => ({
    isInTrimMode: state.trimming?.in_trim_mode,
    areViewerInsightsOpen: selectIsActive(state),
    showPaywall: hasScope(state, ENGAGEMENT_INSIGHTS_PAYWALL),
    hasEngagementInsights: hasScope(state, ENGAGEMENT_INSIGHTS_ACCESS),
    videoId: selectCurrentVideoId(state),
    views: selectVideoViews(state),
  }));

  const {
    isInTrimMode,
    areViewerInsightsOpen,
    showPaywall,
    videoId,
    views,
    hasEngagementInsights,
  } = appState;

  const totalViews = views?.total || 0;
  const distinctViews = views?.distinct || 0;

  if (!totalViews) {
    return <InsightsNoView />;
  }

  useFetchAndPopulateViewerInsights(videoId, hasEngagementInsights);
  const viewerSelector = hasEngagementInsights
    ? selectViewersForEngagementInsights
    : selectViewersForViewerInsights;

  /** @type {any[]} */
  const viewers = useSelector(viewerSelector);
  const loading = hasEngagementInsights && viewers.length < distinctViews;

  viewers.splice(MAX_DISTINCT_VIEWERS);

  const openInsights = viewer => {
    // we need to select the viewer before opening the panel
    // to skip the animation
    dispatch(selectInsightsView(viewer));

    dispatch(updateTheaterMode(false));
    dispatch(endCTAFlow());
    dispatch(endVideoSettingsFlow());
    dispatch(endThumbnailFlow());
    isInTrimMode && dispatch(leaveTrimModeCreator());

    if (!areViewerInsightsOpen) {
      dispatch(toggleViewerInsightsUI());
      trackActivate();
    }

    closeModal();
  };

  const viewsText = [
    `${totalViews} total ${pluralize('view', Number(totalViews))}`,
    `${distinctViews} unique ${pluralize('viewer', Number(distinctViews))}`,
  ].join(', ');

  return (
    <ModalContainer variant={hasEngagementInsights ? null : 'small'}>
      <div className="borderBottom px:large">
        <Text variant="title" as="h2">
          {hasEngagementInsights ? 'Engagement' : 'Viewer'} Insights
        </Text>
        <Text color="bodyDimmed" className="mt:small mb:medium" as="p">
          {viewsText}
        </Text>
      </div>
      <div className="modal-viewer-list px:small pt:medium thin-scrollbars overflow:auto">
        {loading ? (
          <div className="loading text:center py:xlarge">
            <Loader size="large" />
          </div>
        ) : (
          viewers.map((viewer, i) => {
            viewer.onClick = hasEngagementInsights
              ? () => openInsights(viewer)
              : undefined;

            return (
              <ViewerItem
                key={i}
                viewer={viewer}
                hasEngagementInsights={hasEngagementInsights}
              />
            );
          })
        )}
      </div>
      <SeeEngagementButton
        showPaywall={showPaywall}
        hasEngagementInsights={hasEngagementInsights}
        openInsights={() => openInsights(null)}
      />
    </ModalContainer>
  );
};
